import { useEffect, useState } from "react";
import { Box, Grid, TableCell, TableRow } from "@mui/material";
import { useTranslation } from "react-i18next";
import { isFulfilled } from "@reduxjs/toolkit";

import { Heading1 } from "../../../common/components/styled/styled";
import { StripedTable } from "../../../common/components/functional/stripedTable/StripedTable";
import { useAppDispatch } from "../../../app/hooks";
import { fetchOrderQueues } from "../settingsActions";
import OverlaySpinner from "../../../common/components/functional/overlaySpinner/OverlaySpinner";
import AddButton from "../../../common/components/functional/addButton/AddButton";
import SearchField from "../../../common/components/functional/SearchField";
import AddOrderQueue from "./AddOrderQueue";
import { FormContainer } from "../../customer/customerManagement/styles";
import { OrderQueueOption } from "./types";
import { fetchExpertUsers } from "../../user/expertUsersSlice";

interface Props {
  selectQueue: (id: string) => void;
}

export const OrderQueuesList = ({ selectQueue }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const [queues, setQueues] = useState<OrderQueueOption[]>([]);
  const [filteredQueues, setFilteredQueues] = useState<OrderQueueOption[]>([]);
  const [searchString, setSearchString] = useState<string>("");
  const [isAddQueueOpen, setIsAddQueueOpen] = useState<boolean>(false);

  const updateList = (newQueue: OrderQueueOption) => {
    setQueues([...queues, newQueue]);
  };

  useEffect(() => {
    setIsLoading(true);
    Promise.all([
      dispatch(fetchOrderQueues()).then((action) => {
        if (isFulfilled(action)) {
          setQueues(action.payload);
        }
      }),
      dispatch(fetchExpertUsers({})),
    ]).then(() => setIsLoading(false));
  }, [dispatch]);

  useEffect(() => {
    if (searchString) {
      const filteredQueues = queues.filter((q) =>
        q.name.toLowerCase().includes(searchString.toLowerCase()),
      );
      setFilteredQueues(filteredQueues);
    } else {
      setFilteredQueues(queues);
    }
  }, [searchString, queues]);

  const queueRows = filteredQueues.map((q) => (
    <TableRow key={q.id} onClick={() => selectQueue(q.id)}>
      <TableCell>{q.name}</TableCell>
    </TableRow>
  ));

  return (
    <FormContainer>
      <OverlaySpinner isLoading={isLoading} />
      <AddOrderQueue
        isOpen={isAddQueueOpen}
        close={() => setIsAddQueueOpen(false)}
        updateQueueList={updateList}
      />
      <Grid container gap={2}>
        <Grid item container xs={12}>
          <Box display="flex" alignItems="center">
            <Heading1>{t("orderQueues:orderQueues")}</Heading1>
            <AddButton
              label={t("common:add")}
              variant="contained"
              sx={{ marginLeft: 1 }}
              onClick={() => setIsAddQueueOpen(true)}
            />
          </Box>
        </Grid>
        <Grid item xs={12}>
          <SearchField
            label={t("search") || ""}
            value={searchString}
            onChange={(value) => setSearchString(value)}
          />
        </Grid>
        <Grid item xs={12}>
          <StripedTable
            columnHeaders={[t("common:name")]}
            itemRows={queueRows}
          />
        </Grid>
      </Grid>
    </FormContainer>
  );
};
